import React, { useState, useEffect } from 'react';
import { FaUserShield, FaPlus, FaEdit, FaTrash, FaSearch, FaUserSlash, FaUserCheck } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Component for managing system administrators
 * Allows creating, editing, and deleting admin users
 */
const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const { showSuccess, showError } = useToast();
  
  // Fetch admins on component mount
  useEffect(() => {
    fetchAdmins();
  }, []);
  
  // Fetch admins from API
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users?role=system_admin');
      setAdmins(response.data.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      showError('Failed to load administrators');
    } finally {
      setLoading(false);
    }
  };
  
  // Open modal with empty form for new admin
  const handleAddNew = () => {
    setCurrentAdmin(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
    });
    setFormErrors({});
    setModalOpen(true);
  };
  
  // Open modal with admin data for editing
  const handleEdit = (admin) => {
    setCurrentAdmin(admin);
    setFormData({
      firstName: admin.firstName,
      lastName: admin.lastName,
      email: admin.email,
      phoneNumber: admin.phoneNumber,
      password: '',
      confirmPassword: '',
    });
    setFormErrors({});
    setModalOpen(true);
  };
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear specific field error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
  };
  
  // Validate the form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
    } else if (!/^(?:\+254|254|0)[17][0-9]{8}$/.test(formData.phoneNumber)) {
      errors.phoneNumber = 'Phone number is invalid';
    }
    
    // Password validations only if creating new admin or changing password
    if (!currentAdmin || formData.password) {
      if (!currentAdmin && !formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password && formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
      
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const adminData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        role: 'system_admin',
      };
      
      // Add password only if provided (required for new users)
      if (formData.password) {
        adminData.password = formData.password;
      }
      
      let response;
      
      if (currentAdmin) {
        // Update existing admin
        response = await api.put(`/admin/users/${currentAdmin._id}`, adminData);
        showSuccess('Administrator updated successfully');
      } else {
        // Create new admin
        response = await api.post('/admin/users', adminData);
        showSuccess('Administrator created successfully');
      }
      
      // Update admin list with new data
      fetchAdmins();
      setModalOpen(false);
    } catch (error) {
      console.error('Error saving admin:', error);
      
      // Display specific validation errors if available
      if (error.response && error.response.data && error.response.data.errors) {
        setFormErrors(error.response.data.errors);
      } else {
        showError(error.response?.data?.message || 'Error saving administrator');
      }
    }
  };
  
  // Open delete confirmation modal
  const handleDeleteClick = (admin) => {
    setAdminToDelete(admin);
    setDeleteModalOpen(true);
  };
  
  // Process delete admin
  const handleDeleteConfirm = async () => {
    if (!adminToDelete) return;
    
    try {
      await api.delete(`/admin/users/${adminToDelete._id}`);
      showSuccess('Administrator deleted successfully');
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      showError(error.response?.data?.message || 'Error deleting administrator');
    } finally {
      setDeleteModalOpen(false);
      setAdminToDelete(null);
    }
  };
  
  // Handle account status change (activate/deactivate)
  const handleToggleStatus = async (admin) => {
    try {
      await api.put(`/admin/users/${admin._id}/status`, {
        isActive: !admin.isActive,
      });
      
      showSuccess(`Administrator ${admin.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchAdmins();
    } catch (error) {
      console.error('Error updating admin status:', error);
      showError(error.response?.data?.message || 'Error updating administrator status');
    }
  };
  
  // Filter admins based on search term
  const filteredAdmins = admins.filter((admin) => {
    const fullName = `${admin.firstName} ${admin.lastName}`.toLowerCase();
    const search = searchTerm.toLowerCase();
    
    return (
      fullName.includes(search) ||
      admin.email.toLowerCase().includes(search) ||
      admin.phoneNumber.includes(search)
    );
  });
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaUserShield className="mr-2 text-primary" /> Administrator Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage system administrators and their permissions
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <button
            onClick={handleAddNew}
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> Add Administrator
          </button>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-soft p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center">
          <div className="relative flex-grow mb-4 md:mb-0">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              className="form-input pl-10 py-2"
              placeholder="Search administrators..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>
      
      {/* Admins Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          {filteredAdmins.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">No administrators found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAdmins.map((admin) => (
                    <tr key={admin._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {admin.profileImage ? (
                              <img
                                className="h-10 w-10 rounded-full"
                                src={admin.profileImage}
                                alt={`${admin.firstName} ${admin.lastName}`}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center">
                                {admin.firstName.charAt(0)}
                                {admin.lastName.charAt(0)}
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {admin.firstName} {admin.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              System Administrator
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{admin.email}</div>
                        <div className="text-sm text-gray-500">{admin.phoneNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          admin.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {admin.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(admin.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(admin)}
                          className="text-primary hover:text-primary-600 mr-3"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(admin)}
                          className={`${
                            admin.isActive ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'
                          } mr-3`}
                          title={admin.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {admin.isActive ? <FaUserSlash /> : <FaUserCheck />}
                        </button>
                        <button
                          onClick={() => handleDeleteClick(admin)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Add/Edit Admin Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={currentAdmin ? 'Edit Administrator' : 'Add New Administrator'}
        size="lg"
      >
        <div className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="firstName" className="form-label">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`form-input ${formErrors.firstName ? 'border-red-500' : ''}`}
                />
                {formErrors.firstName && (
                  <p className="form-error">{formErrors.firstName}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="lastName" className="form-label">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`form-input ${formErrors.lastName ? 'border-red-500' : ''}`}
                />
                {formErrors.lastName && (
                  <p className="form-error">{formErrors.lastName}</p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-input ${formErrors.email ? 'border-red-500' : ''}`}
                />
                {formErrors.email && (
                  <p className="form-error">{formErrors.email}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="phoneNumber" className="form-label">
                  Phone Number
                </label>
                <input
                  type="text"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className={`form-input ${formErrors.phoneNumber ? 'border-red-500' : ''}`}
                  placeholder="+254xxxxxxxxx"
                />
                {formErrors.phoneNumber && (
                  <p className="form-error">{formErrors.phoneNumber}</p>
                )}
              </div>
            </div>
            
            {/* Password fields (required for new admins, optional for edits) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label htmlFor="password" className="form-label">
                  {currentAdmin ? 'New Password (leave blank to keep current)' : 'Password'}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`form-input ${formErrors.password ? 'border-red-500' : ''}`}
                />
                {formErrors.password && (
                  <p className="form-error">{formErrors.password}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`form-input ${formErrors.confirmPassword ? 'border-red-500' : ''}`}
                />
                {formErrors.confirmPassword && (
                  <p className="form-error">{formErrors.confirmPassword}</p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="btn-outline mr-2"
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                {currentAdmin ? 'Update Administrator' : 'Add Administrator'}
              </button>
            </div>
          </form>
        </div>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Delete"
        size="sm"
      >
        <div className="p-6">
          <p className="text-gray-700 mb-6">
            Are you sure you want to delete administrator <strong>{adminToDelete ? `${adminToDelete.firstName} ${adminToDelete.lastName}` : ''}</strong>? This action cannot be undone.
          </p>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setDeleteModalOpen(false)}
              className="btn-outline mr-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteConfirm}
              className="bg-red-500 hover:bg-red-700 text-white font-medium py-2 px-4 rounded"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminManagement;
